/* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-runloop */
import { isDestroyed, isDestroying } from '@ember/destroyable';
import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { cancel, debounce, next, scheduleOnce } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismButton,
  PrismComboSelectInput,
  PrismIcon,
  PrismSelectInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq } from 'ember-truth-helpers';
import type { Ota } from 'frontend-utils/models';
import { ROOMTYPES } from 'frontend-utils/models/roomtype';
import {
  OTA_IDS,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import registerObserver from 'frontend-utils/utils/register-observer';
import cloneDeep from 'lodash.clonedeep';
import GeneralSelectionsMealPlan from 'lumen/components/general/selections/meal-plan';
import HotelDropdownsCompsetSelection from 'lumen/components/hotel/dropdowns/compset-selection';
import HotelAndMarketDropdownsSelectionOtas from 'lumen/components/hotel-and-market/dropdowns/selection-otas';
import MarketRatesButtonsButtonCompare from 'lumen/components/market/rates/buttons/button-compare';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  COMPARETYPES,
  COMPARETYPES_TO_QUERYPARAMS,
  COMPARETYPES_TO_TRANSLATIONMAPPINGS,
  DATE_TRANSLATION_KEYS,
  MAX_LOS_BOOKING,
  MAX_LOS_EXPEDIA,
  MEALTYPE_ICONS,
  MEALTYPE_TRANSLATION_KEYS,
  MEALTYPES,
  MEMBERSHIPTYPE_TRANSLATION_KEYS,
  MEMBERSHIPTYPES,
  OTA_IDS_WITH_4_GUESTS,
  PLATFORM_TRANSLATION_KEYS,
  RATE_INSIGHT_FILTER_TYPE_LOS,
  RATE_INSIGHT_FILTER_TYPE_MEALTYPE,
  RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
  RATE_INSIGHT_FILTER_TYPE_PERSONS,
  RATE_INSIGHT_FILTER_TYPE_PLATFORM,
  ROOMTYPE_TRANSLATION_KEYS,
  SWLY_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import { getRateParamOptions } from 'lumen/utils/rates-selections';
import { sortRoomtypeIds } from 'lumen/utils/sort-ids';
import PerfectScrollbar from 'perfect-scrollbar';

export const AVAILABLE_MEAL_PLANS = [
  MEALTYPES.ANY,
  MEALTYPES.ROOM_ONLY,
  MEALTYPES.BED_AND_BREAKFAST,
  MEALTYPES.HALF_BOARD,
  MEALTYPES.FULL_BOARD,
  MEALTYPES.ALL_IN,
];

export const COMPARABLE_MEAL_PLANS = [
  MEALTYPES.ROOM_ONLY,
  MEALTYPES.BED_AND_BREAKFAST,
  MEALTYPES.HALF_BOARD,
  MEALTYPES.FULL_BOARD,
  MEALTYPES.ALL_IN,
];

const SCROLLABLE_HEADER_MARGINS = 40;
const DEFAULT_LOS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

interface HotelAndMarketRatesOtaSelectionsRatesSelectionsSignature {
  Args: {
    compareDisabledTooltipText: unknown;
    isCompareDisabled: unknown;
    isMonthInPast: unknown;
    isRoomtypeRequestLoading: unknown;
    isTableViewActive: unknown;
    platform: unknown;
    rateParam: unknown;
    selectedLos: unknown;
    selectedMaxPersons: unknown;
    selectedMealPlan: unknown;
    selectedMembershipType: unknown;
    selectedOTAId: unknown;
    selectedPlatform: unknown;
    selectedRoomType: unknown;
    selectionsChanged: unknown;
    setCompsetId: unknown;
    type: unknown;
  };
}

export default class HotelAndMarketRatesOtaSelectionsRatesSelections extends Component<HotelAndMarketRatesOtaSelectionsRatesSelectionsSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  availableMembershipTypeFilters = [
    // API mapping: https://git.mylighthouse.com/OTA-Insight/schemaregistry/blob/master/protobuf/rates.proto#L11
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(
        MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.PUBLIC],
      ),
      id: MEMBERSHIPTYPES.PUBLIC,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(
        MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.MEMBER_ONLY],
      ),
      id: MEMBERSHIPTYPES.MEMBER_ONLY,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.AAA]),
      id: MEMBERSHIPTYPES.AAA,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(
        MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.SENIOR],
      ),
      id: MEMBERSHIPTYPES.SENIOR,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(
        MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.GOVERNMENT],
      ),
      id: MEMBERSHIPTYPES.GOVERNMENT,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.AARP]),
      id: MEMBERSHIPTYPES.AARP,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(
        MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.MILITARY],
      ),
      id: MEMBERSHIPTYPES.MILITARY,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
    {
      // @ts-expect-error: Incorrect type
      label: this.intl.t(
        MEMBERSHIPTYPE_TRANSLATION_KEYS[MEMBERSHIPTYPES.RESIDENT],
      ),
      id: MEMBERSHIPTYPES.RESIDENT,
      type: RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE,
      isComparable: true,
    },
  ];
  defaultPersonsOptions = [1, 2];
  disableLeftArrow = true;
  disableRightArrow = false;
  elRatesNavbarScroller = null;
  perfectScrollbarInstance = null;
  runDebounce = null;
  runNext = null;
  showArrows = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableLos() {
    const ota = this.selectedOTA;
    let options = [...(this.hotelOrMarket?.defaultLosRates ?? [])];
    if (ota) {
      if (
        ota.hasLosFilter &&
        this.access.hasRatesCustomLiveupdate &&
        this.selectedOTA?.hasLiveShop
      ) {
        options = options.concat(DEFAULT_LOS);
      }
      if (ota.id === OTA_IDS.BOOKINGDOTCOM || ota.id === OTA_IDS.EXPEDIA) {
        let maxLos;
        if (ota.id === OTA_IDS.BOOKINGDOTCOM) {
          maxLos = MAX_LOS_BOOKING;
        } else if (ota.id === OTA_IDS.EXPEDIA) {
          maxLos = MAX_LOS_EXPEDIA;
        }
        // @ts-expect-error: Incorrect type
        for (let i = 1; i <= maxLos; i++) {
          options.push(i);
        }
      }
      // Addon package for extra LOS shopping on GDS
      if (ota.id === OTA_IDS.GDS) {
        options = [];
        if (this.access.hasFeature('rates.gds_default_los')) {
          // @ts-expect-error: Incorrect type
          options.push(this.hotelOrMarket?.defaultLosRates?.[0]); // Only take the first default LOS rates for GDS
        }
        if (this.access.hasFeature('rates.addons.gds_los_5_7')) {
          options.push(5);
          options.push(7);
        }
        if (this.access.hasFeature('rates.addons.gds_los_7_30')) {
          options.push(7);
          options.push(30);
        }
      }
      return [...new Set(options)]
        .sort((a, b) => a - b)
        .map((LOSOption) => ({
          id: LOSOption,
          label: this.intl.t('rates.nights', { count: LOSOption }),
          type: RATE_INSIGHT_FILTER_TYPE_LOS,
          isComparable: LOSOption !== 1,
        }));
    }
    return [
      {
        id: 1,
        label: this.intl.t('rates.nights', { count: 1 }),
        type: RATE_INSIGHT_FILTER_TYPE_LOS,
        isComparable: false,
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableMealPlans() {
    return AVAILABLE_MEAL_PLANS.map((mealPlan) => ({
      id: mealPlan,
      label: this.intl.t(MEALTYPE_TRANSLATION_KEYS[mealPlan]),
      icon: MEALTYPE_ICONS[mealPlan],
      type: RATE_INSIGHT_FILTER_TYPE_MEALTYPE,
      // @ts-expect-error: Incorrect type
      isComparable: COMPARABLE_MEAL_PLANS.includes(mealPlan),
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availablePlatforms() {
    if (
      this.access.hasFeature('rates.mobile') &&
      // @ts-expect-error: Incorrect type
      this.selectedOTA.hasMobileFilter
    ) {
      return [
        {
          label: this.intl.t(
            PLATFORM_TRANSLATION_KEYS[PLATFORMS.PLATFORM_UNSPECIFIED],
          ),
          id: PLATFORMS.PLATFORM_UNSPECIFIED,
          type: RATE_INSIGHT_FILTER_TYPE_PLATFORM,
          isComparable: true,
        },
        {
          label: this.intl.t(
            PLATFORM_TRANSLATION_KEYS[PLATFORMS.PLATFORM_MOBILE_WEBSITE],
          ),
          id: PLATFORMS.PLATFORM_MOBILE_WEBSITE,
          type: RATE_INSIGHT_FILTER_TYPE_PLATFORM,
          isComparable: true,
        },
        {
          label: this.intl.t(
            PLATFORM_TRANSLATION_KEYS[PLATFORMS.PLATFORM_MOBILE_APP],
          ),
          id: PLATFORMS.PLATFORM_MOBILE_APP,
          type: RATE_INSIGHT_FILTER_TYPE_PLATFORM,
          isComparable: true,
        },
      ].filter(
        (platform) =>
          // @ts-expect-error: Incorrect type
          this.selectedOTA.mappedSupportedPlatforms.indexOf(platform.id) !== -1,
      );
    }
    return [];
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableRoomTypes() {
    if (this.args.isRoomtypeRequestLoading) {
      return [];
    }

    // @ts-expect-error: Incorrect type
    const output = [];
    // @ts-expect-error: Incorrect type
    this.getRoomtypes().forEach((x) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (x.id && x.name) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        if (x.id.indexOf('c_') === 0) {
          // Custom roomtypes, add as is
          output.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            label: x.name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            id: x.id,
            type: COMPARETYPES.ROOMTYPE,
            isComparable: this.hotel !== null, // Only for single property
          });
        } else {
          // Default roomtypes, translate
          output.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            label: this.intl.t(`rates.roomtypes.${x.name}`),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            id: x.id,
            type: COMPARETYPES.ROOMTYPE,
            isComparable: this.hotel !== null, // Only for single property
          });
        }
      }
    });
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    output.sort((a, b) => sortRoomtypeIds(a.id, b.id));
    output.unshift({
      label: this.intl.t(ROOMTYPE_TRANSLATION_KEYS[ROOMTYPES.ALL]),
      id: ROOMTYPES.ALL,
      type: COMPARETYPES.ROOMTYPE,
      isComparable: false,
    });
    return output;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCompsetFilter() {
    return this.hotelAndMarketService.hasSecondaryCompset;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasLosFilter() {
    return this.availableLos.length > 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMembershipTypeFilter() {
    return this.selectedOTA?.hasMemberFilter;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPersonsFilter() {
    return this.selectedOTA?.hasPersonFilter;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRateChanges() {
    return this.selectedOTA?.hasRatechange && !this.args.isMonthInPast;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // Checks if user is comparing depending on compare QPs.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isComparing() {
    // @ts-expect-error: Incorrect type
    const { queryParams } = this.router.currentRoute;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { compareType, compareValue } = queryParams;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return compareType && compareValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxPersonsOptions() {
    if (this.selectedOTA?.hasPersonFilter) {
      let arr = cloneDeep(this.defaultPersonsOptions);
      if (this.args.selectedOTAId === OTA_IDS.BOOKINGDOTCOM) {
        arr = [...arr, 3, 4, 6];
        // @ts-expect-error: Incorrect type
      } else if (OTA_IDS_WITH_4_GUESTS.indexOf(this.args.selectedOTAId) > -1) {
        arr = [...arr, 3, 4];
      }
      return arr.map((maxPersonsOption) => ({
        id: maxPersonsOption,
        label: this.intl.t('rates.persons_selection', {
          count: maxPersonsOption,
        }),
        type: RATE_INSIGHT_FILTER_TYPE_PERSONS,
        isComparable: true,
      }));
    }
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelinfo() {
    return this.hotel?.hotelinfo;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateParams() {
    return getRateParamOptions(
      this.selectedOTA,
      // @ts-expect-error: Incorrect type
      this.args.platform,
      this.intl,
      this.hotelAndMarketService,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesCompareText() {
    // @ts-expect-error: Incorrect type
    const { queryParams } = this.router.currentRoute;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { compareType, compareValue } = queryParams;

    // Get the queryparam associated with the given comparetype.
    // E.g. the 'mealtype' comparetype has QP 'mealPlan' (that's why we need a mapping for this).
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const queryParam = COMPARETYPES_TO_QUERYPARAMS[compareType];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const queryParamValue =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      queryParams[queryParam] ?? DEFAULT_QP_VALUES.application[queryParam];

    let filterString;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const comparisonString = this.getPrettyFilterName(
      compareType,
      compareValue,
    );

    // Use 'Selected date' as filterString for SWLY and date comparetypes, otherwise get pretty name.
    if (
      compareType === COMPARETYPES.SWLY ||
      compareType === COMPARETYPES.DATE
    ) {
      filterString = this.intl.t(SWLY_TRANSLATION_KEYS.SELECTED_DATE);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      filterString = this.getPrettyFilterName(compareType, queryParamValue);
    }

    // Old top-text behavior / Market Rates: 'Comparing: {{a}} vs. {{b}}'
    // return this.intl.t('rates.compare.comparing', { a: filterString, b: comparisonString });

    // We've made a small change here to support the new Hotel Rates compare text pill
    return this.intl.t('rates.compare.compare_text.a_vs_b', {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      a: filterString,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      b: comparisonString,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedLosValue() {
    return this.availableLos.find((los) => los.id === this.args.selectedLos);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMaxPersonsValue() {
    return this.maxPersonsOptions.find(
      (option) => option.id === this.args.selectedMaxPersons,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMembershipTypeValue() {
    return this.availableMembershipTypeFilters.find(
      (filter) => filter.id === this.args.selectedMembershipType,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOTA() {
    // @ts-expect-error: Incorrect type
    return this.store.peekRecord<Ota>('ota', this.args.selectedOTAId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPlatformItem() {
    return this.availablePlatforms.find(
      (platform) => platform.id === this.args.selectedPlatform,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRateType() {
    return this.rateParams.find(
      (rateParam) => rateParam.id === this.args.rateParam,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRoomTypeItem() {
    if (
      this.availableRoomTypes?.length > 0 &&
      this.availableRoomTypes.find(
        (roomType) => roomType.id === this.args.selectedRoomType,
      )
    ) {
      return this.availableRoomTypes.find(
        (roomType) => roomType.id === this.args.selectedRoomType,
      );
    }
    return {
      label: this.intl.t('rates.roomtypes.all'),
    };
  }

  // Note: all compare getters below are to be able to have a temp custom compare filter in Hotel Rates
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showCompareFilterPill() {
    // The filter pill should only be shown for Hotel Rates on the table view, when the compare is active
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.hotel && this.isComparing;
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    registerObserver(this, ['availableRoomTypes.[]'], this.roomtypeObserver);
    registerObserver(
      this,
      ['args.activeView', 'hasRateChanges', 'showArrows'],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.onViewChange,
    );
  }

  willDestroy(): void {
    super.willDestroy();

    if (this.runDebounce) {
      cancel(this.runDebounce);
      set(this, 'runDebounce', null);
    }
    if (this.runNext) {
      cancel(this.runNext);
      set(this, 'runNext', null);
    }

    this.elRatesNavbarScroller = null;

    if (this.perfectScrollbarInstance) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.perfectScrollbarInstance.destroy();
      set(this, 'perfectScrollbarInstance', null);
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    window.removeEventListener('resize', this.resizeHandler);
  }

  // Same code as in the top-text component. Moved here to be able to generate the Compare text in this component for Hotel Rates.
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getPrettyFilterName(compareType, filterValue) {
    // The translation mapping to use for the given compare type.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const translationMapping = COMPARETYPES_TO_TRANSLATIONMAPPINGS[compareType];

    // Get filter translation key from the translation mapping and return pretty name.
    switch (compareType) {
      case COMPARETYPES.LOS:
      case COMPARETYPES.PERSONS: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment
        return this.intl.t(translationMapping, { count: filterValue });
      }

      case COMPARETYPES.DATE: {
        const count = this.hotelAndMarketService
          .getLocalTime()
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          .diff(filterValue, 'day');
        return this.intl.t(DATE_TRANSLATION_KEYS.DAYS_AGO, { count });
      }

      case COMPARETYPES.ROOMTYPE: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const filterTranslationKey = translationMapping[filterValue];

        // Handle custom roomtype names, these have no translations because they are custom.
        if (!filterTranslationKey) {
          if (this.hotelAndMarketService.hotel) {
            const roomTypes =
              this.hotelAndMarketService.hotel?.hotelinfo?.getRoomTypesForOTA(
                // @ts-expect-error: Incorrect type
                this.args.selectedOTAId,
              );
            const roomType = roomTypes.find((rt) => rt.id === filterValue);

            if (roomType) {
              return roomType.name;
            }
          }

          // return compareValue (roomtype id) if roomtype isn't found.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return filterValue;
        }

        // Return translation if roomtype is not custom.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return this.intl.t(filterTranslationKey);
      }

      default: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const filterTranslationKey = translationMapping[filterValue];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return this.intl.t(filterTranslationKey);
      }
    }
  }

  // Merge roomtypeSelections of all roomtypes in the store that belong to the active compset
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRoomtypes() {
    // First try to fetch the roomtypes for the own hotelinfo
    const roomtypesForOwnHotelInfo = this.ownHotelinfo?.getRoomTypesForOTA(
      // @ts-expect-error: Incorrect type
      this.args.selectedOTAId,
    );
    // @ts-expect-error: Incorrect type
    if (roomtypesForOwnHotelInfo.length) {
      return roomtypesForOwnHotelInfo;
    }

    // @ts-expect-error: Incorrect type
    const uniqueRoomtypeSelections = [];
    const roomtypeSelectionsIds = new Set();

    // In case the own hotelinfo has no roomtypes (e.g. pre-opening hotel), fetch the roomtypes of the competitors instead
    this.hotelAndMarketService.primaryAndSecondaryCompetitors.forEach(
      (hotelinfo) => {
        // @ts-expect-error: Incorrect type
        hotelinfo.getRoomTypesForOTA(this.args.selectedOTAId).forEach((rt) => {
          if (!roomtypeSelectionsIds.has(rt.id)) {
            roomtypeSelectionsIds.add(rt.id);
            uniqueRoomtypeSelections.push(rt);
          }
        });
      },
    );

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return uniqueRoomtypeSelections;
  }

  onViewChange(): void {
    if (this.showArrows) {
      if (this.elRatesNavbarScroller) {
        // @ts-expect-error: Incorrect type
        this.elRatesNavbarScroller.scrollLeft = 0;
      }

      set(this, 'disableLeftArrow', true);
      set(this, 'disableRightArrow', false);
    }

    const runNext = next(() => {
      // The selection options can change depending on the view so we need to
      // trigger a resize in order to get the correct scrollWidth
      scheduleOnce('afterRender', this, function () {
        // get(this, 'resizeHandlerNoDebounceFn')();

        // Currently solves timing issue when switching from rates page to the
        // rates table on small screen (ratechanges are added)
        // Without debounce the resize can be executed too early (power-select
        // values not yet present, so the width is incorrect)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.resizeHandlerDebouncedFn();
      });
    });
    set(this, 'runNext', runNext);
  }

  roomtypeObserver(): void {
    if (this.availableRoomTypes && this.availableRoomTypes?.length > 0) {
      // Clear the room type filter if the (custom) room type is not available for the current selection
      if (
        !this.availableRoomTypes.find(
          (roomType) => roomType.id === this.args.selectedRoomType,
        )
      ) {
        // @ts-expect-error: Incorrect type
        this.args.selectionsChanged('roomType', ROOMTYPES.ALL);
      }
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  chooseOTA(ota): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.args.selectionsChanged('ota', ota.id);
  }

  @action
  removeCompare(): void {
    // Remove compare QPs from URL to remove the compare.
    this.router.transitionTo({
      queryParams: {
        compareType: null,
        compareValue: null,
      },
    });
  }

  @action
  scrollLeft(): void {
    if (!this.disableLeftArrow) {
      setTimeout(() => {
        if (this.elRatesNavbarScroller) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.elRatesNavbarScroller.scroll({
            // Potential fallback fix if scroll right isn't fully correct: add extra margin here
            // Test screen width to reproduce potential issues: +/- 1135px
            left: 0,
            // behavior: 'smooth' is much nicer, but gave issues in FF :( - investigate later
            behavior: 'auto',
          });
        }

        set(this, 'disableRightArrow', false);
        set(this, 'disableLeftArrow', true);
      }, 0);
    }
  }

  @action
  scrollRight(): void {
    if (!this.disableRightArrow) {
      setTimeout(() => {
        if (this.elRatesNavbarScroller) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.elRatesNavbarScroller.scroll({
            // Potential fallback fix if scroll right isn't fully correct: add extra margin here
            // Test screen width to reproduce potential issues: +/- 1135px
            // @ts-expect-error: Incorrect type
            left: this.navbarElScrollWidth - this.navbarElClientWidth,
            // behavior: 'smooth' is much nicer, but gave issues in FF :( - investigate later
            behavior: 'auto',
          });
        }

        set(this, 'disableRightArrow', true);
        set(this, 'disableLeftArrow', false);
      }, 0);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  selectionsChanged(queryParam, value): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.args.selectionsChanged(queryParam, value.id);
  }

  @action
  setupScrollbar(): void {
    scheduleOnce('afterRender', this, () => {
      const elRatesNavbarScroller = document.querySelector(
        '.rates-navbar-scroller',
      );

      set(this, 'elRatesNavbarScroller', elRatesNavbarScroller);

      set(
        this,
        'perfectScrollbarInstance',
        // @ts-expect-error: Incorrect type
        new PerfectScrollbar(this.elRatesNavbarScroller, {
          suppressScrollY: true,
          suppressScrollX: false,
          /* x-rail element is set to display:none in the component CSS file */
        }),
      );
    });

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const resizeFunctionHeaderScroller = () => {
      const navbarScrollEl = this.elRatesNavbarScroller;

      if (navbarScrollEl) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.perfectScrollbarInstance.update();

        // Note: Bit risky that we need to check for a CSS class here, but no current workaround
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        if (navbarScrollEl.classList.contains('ps--active-x')) {
          set(this, 'showArrows', true);
        } else {
          set(this, 'showArrows', false);
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        let navbarScrollClientWidth = navbarScrollEl.clientWidth;

        if (this.showArrows) {
          // if the screen is to small by default, subtract the extra margins
          // added for the arrows from the clientWidth
          navbarScrollClientWidth -= SCROLLABLE_HEADER_MARGINS;
        }

        set(this, 'navbarElClientWidth', navbarScrollClientWidth);
        // @ts-expect-error: Incorrect type
        set(this, 'navbarElScrollWidth', navbarScrollEl.scrollWidth);
      }
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const resizeHandler = () => {
      if (isDestroyed(this) || isDestroying(this)) {
        return;
      }

      const runDebounce = debounce(this, resizeFunctionHeaderScroller, 50);

      set(this, 'runDebounce', runDebounce);
    };

    set(this, 'resizeHandler', resizeHandler);
    window.addEventListener('resize', resizeHandler);
    set(this, 'resizeHandlerNoDebounceFn', resizeFunctionHeaderScroller);
    set(this, 'resizeHandlerDebouncedFn', resizeHandler);

    // Trigger once initially
    resizeHandler();
  }

  <template>
    <div
      class="rates-navbar flex flex-row justify-between"
      {{didInsert this.setupScrollbar}}
    >
      <div class="rates-navbar__selections flex items-center justify-center">
        {{#if this.showArrows}}
          <button
            class="arrow-scrolling {{if this.disableLeftArrow 'disabled'}}"
            type="button"
            {{on "click" this.scrollLeft}}
          >
            <PrismIcon @name="chevron-left" @size="large" />
          </button>
        {{/if}}
        <div
          class="ota-selections-container rates-navbar-scroller gap-200
            {{if this.showArrows 'scrolling-enabled'}}"
        >
          <PrismSelectInput
            @onChange={{fn this.selectionsChanged "rateParam"}}
            @options={{this.rateParams}}
            @value={{this.selectedRateType}}
          />
          <HotelAndMarketDropdownsSelectionOtas
            @includeAllChannels={{true}}
            @onlyAllowMatchedOtas={{true}}
            @selectOta={{this.chooseOTA}}
            {{! @glint-expect-error: Incorrect type }}
            @selectedOTA={{this.selectedOTA}}
            {{! @glint-expect-error: Incorrect type }}
            @type={{@type}}
          />
          {{#if this.availablePlatforms.length}}
            {{#if (eq this.availablePlatforms.length 1)}}
              <p class="select-wrapper no-select">
                {{! @glint-expect-error: Incorrect type }}
                {{get this.availablePlatforms "0.label"}}
              </p>
            {{else}}
              <div class="ic-product-tour-parity-platform-split">
                <PrismSelectInput
                  @onChange={{fn this.selectionsChanged "platform"}}
                  @options={{this.availablePlatforms}}
                  @value={{this.selectedPlatformItem}}
                />
              </div>
            {{/if}}
          {{/if}}
          {{! @glint-expect-error: Incorrect type }}
          {{#if this.market.brands}}
            {{! @glint-expect-error: Incorrect type }}
            {{#if this.hasOnlyOneBrand}}
              <p class="select-wrapper no-select">
                {{! @glint-expect-error: Incorrect type }}
                {{this.onlyBrandName}}
              </p>
            {{else}}
              <div class="select-wrapper">
                <PrismComboSelectInput
                  {{! @glint-expect-error: Incorrect type }}
                  @buildLabelWithCount={{this.brandsBuildLabelWithCount}}
                  {{! @glint-expect-error: Incorrect type }}
                  @onChange={{this.chooseBrand}}
                  {{! @glint-expect-error: Incorrect type }}
                  @options={{this.possibleBrands}}
                  @selectAllLabel={{t "market.brand.count.any"}}
                  {{! @glint-expect-error: Incorrect type }}
                  @value={{this.brandsValue}}
                />
              </div>
            {{/if}}
          {{/if}}
          {{! @glint-expect-error: Incorrect type }}
          {{#if this.market.managementTypes}}
            {{! @glint-expect-error: Incorrect type }}
            {{#if this.hasOnlyOneManagementType}}
              <p class="select-wrapper no-select">
                {{! @glint-expect-error: Incorrect type }}
                {{this.onlyManagementTypeName}}
              </p>
            {{else}}
              <div class="select-wrapper">
                <PrismComboSelectInput
                  {{! @glint-expect-error: Incorrect type }}
                  @buildLabelWithCount={{this.managementTypesBuildLabelWithCount}}
                  {{! @glint-expect-error: Incorrect type }}
                  @onChange={{this.chooseManagementType}}
                  {{! @glint-expect-error: Incorrect type }}
                  @options={{this.possibleManagementTypes}}
                  @selectAllLabel={{t "market.management_type.count.any"}}
                  {{! @glint-expect-error: Incorrect type }}
                  @value={{this.managementTypesValue}}
                />
              </div>
            {{/if}}
          {{/if}}
          {{#if this.hasMembershipTypeFilter}}
            <PrismSelectInput
              @onChange={{fn this.selectionsChanged "membershipType"}}
              @options={{this.availableMembershipTypeFilters}}
              @value={{this.selectedMembershipTypeValue}}
            />
          {{/if}}
          {{#if this.hasLosFilter}}
            <PrismSelectInput
              @onChange={{fn this.selectionsChanged "los"}}
              @options={{this.availableLos}}
              @value={{this.selectedLosValue}}
            />
          {{else}}
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{! @glint-expect-error: Incorrect type }}
                {{t "rates.nights" count=(get this.availableLos "0.id")}}
              </div>
            </div>
          {{/if}}
          {{#if this.hasPersonsFilter}}
            <PrismSelectInput
              @onChange={{fn this.selectionsChanged "maxPersons"}}
              @options={{this.maxPersonsOptions}}
              @value={{this.selectedMaxPersonsValue}}
            />
          {{else}}
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{t
                  "rates.persons_selection"
                  count=this.hotelAndMarketService.hotelOrMarket.defaultPersons
                }}
              </div>
            </div>
          {{/if}}
          {{#if this.selectedOTA.hasRoomtypeFilter}}
            <div
              class="select-wrapper ic-product-tour-competitor-room-mapping-settings"
            >
              <PrismComboSelectInput
                {{! @glint-expect-error: Incorrect type }}
                @disabled={{@isRoomtypeRequestLoading}}
                @onChange={{fn this.selectionsChanged "roomType"}}
                @options={{this.availableRoomTypes}}
                @unsafe_dropdownClass="room-type-select-dropdown--max-width"
                @value={{this.selectedRoomTypeItem}}
              />
              {{#if @isRoomtypeRequestLoading}}
                <PrismTooltip
                  @side="bottom"
                  @text={{t "general.filter_loading"}}
                />
              {{/if}}
            </div>
          {{else}}
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{t "rates.roomtypes.all"}}
              </div>
            </div>
          {{/if}}
          {{#if this.selectedOTA.hasMealplanFilter}}
            <GeneralSelectionsMealPlan
              @allMealPlans={{AVAILABLE_MEAL_PLANS}}
              {{! @glint-expect-error: Incorrect type }}
              @onChange={{fn @selectionsChanged "mealPlan"}}
              {{! @glint-expect-error: Incorrect type }}
              @selectedMealPlan={{@selectedMealPlan}}
            />
          {{else}}
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{t "rates.meal.any"}}
              </div>
            </div>
          {{/if}}

          {{#if (and this.hotel this.hasCompsetFilter)}}
            <div class="select-wrapper">
              <HotelDropdownsCompsetSelection
                @hideCompsetsWithNoData={{true}}
                {{! @glint-expect-error: Incorrect type }}
                @selectedOTA={{this.selectedOTA}}
                {{! @glint-expect-error: Incorrect type }}
                @setCompsetId={{@setCompsetId}}
              />
            </div>
          {{/if}}
        </div>
        {{#if this.showArrows}}
          <button
            class="arrow-scrolling {{if this.disableRightArrow 'disabled'}}"
            type="button"
            {{on "click" this.scrollRight}}
          >
            <PrismIcon @name="chevron-right" @size="large" />
          </button>
        {{/if}}
      </div>

      {{! New temp code to have the Compare filter pill in the Hotel Rate Insight layout changes }}
      {{#if @isTableViewActive}}
        {{#if this.showCompareFilterPill}}
          <div class="compare-selections-filter-pill flex flex-row gap-50">
            <div class="ml-200 flex items-center text-nowrap">
              <span class="text-200--bold">{{this.ratesCompareText}}</span>
            </div>
            <div class="mr-50 ml-50 flex items-center justify-center">
              <PrismButton
                @buttonType="ghost"
                @icon="cross"
                @onClick={{this.removeCompare}}
                @size="small"
              />
            </div>
          </div>
        {{else}}
          <MarketRatesButtonsButtonCompare
            @availableLos={{this.availableLos}}
            @availableMealPlans={{this.availableMealPlans}}
            @availableMembershipTypeFilters={{this.availableMembershipTypeFilters}}
            @availablePlatforms={{this.availablePlatforms}}
            @availableRoomTypes={{this.availableRoomTypes}}
            @compareDisabledTooltipText={{@compareDisabledTooltipText}}
            {{! @glint-expect-error: Incorrect type }}
            @defaultPersons={{this.hotelAndMarketService.hotelOrMarket.defaultPersons}}
            @hasMealplanFilter={{this.selectedOTA.hasMealplanFilter}}
            @hasMembershipTypeFilter={{this.hasMembershipTypeFilter}}
            @hasPersonsFilter={{this.hasPersonsFilter}}
            @hasRoomtypeFilter={{this.selectedOTA.hasRoomtypeFilter}}
            @isCompareDisabled={{@isCompareDisabled}}
            @isTableViewActive={{@isTableViewActive}}
            @maxPersonsOptions={{this.maxPersonsOptions}}
            @rateParams={{this.rateParams}}
          />
        {{/if}}
      {{/if}}
    </div>
  </template>
}
